import { ResourceState, ResourceStatuses } from '@platform/shared/resource.state';

import { MobileUploadDto } from '@core/mobile-upload/mobile-upload.model';
import { DateOnly } from '@platform/shared/date-only';
import { Permission } from '@platform/platform.state';

export type RoleName = 'CustomerSpaceAdmin' | 'Employee';
export type UserLanguage = 'German' | 'English';
export type UserNotificationInterval = 'Weeks1' | 'Hours24' | 'Hours12' | 'Hours4' | 'Hours1' | 'Minutes15';
export type NotificationType =
  | 'CustomerSpaceDocument'
  | 'CustomerSpaceAssignment'
  | 'ProjectTask'
  | 'PortalTask'
  | 'PortalTaskComment'
  | 'ProjectDocument'
  | 'ProjectAssignment'
  | 'DocumentComment'
  | 'FileProcessAssignment';

export interface CustomerSpace {
  id: string;
  name: string;
  isActive: boolean;
  abacusMandator: AbacusMandator;
  notificationCount: number;
  openTaskCount: number;
  isFavorite: boolean;
}

export interface CustomerSpaceAdmin {
  id: string;
  name: string;
  erpAddressNumber: string;
  erpProjectNumbers: string;
  isActive: boolean;
  abacusMandator: AbacusMandator;
  users: CustomerSpaceUser[];
  roles: Role[];
  deletionBlockers: string[];
  deactivationComment: string;
  deactivationTime: Date;
  deactivatedBy: string;
}

export interface AbacusMandator {
  abacusInstanceId: string;
  abacusMandatorId: string;
  myAbacusInboxUrl: string;
  abacusErpUrl: string;
  abacusObtErpUrl: string;
}

export interface UserBase {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  organizationUnit: string;
  mobileNumber: string;
  mail: string;
  isInternal: boolean;
  isActive: boolean;
  isDeputy: boolean;
  deputyFromName: string | null;
}

export interface CustomerSpaceUser {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  organizationUnit: string;
  mobileNumber: string;
  mail: string;
  isInternal: boolean;
  isActive: boolean;
  customerSpaceRole: Role;
  appPermissions: string[];
  lockedAppPermissions: string[];
  isContact: boolean;
  isMailSent: boolean;
  isDeputy: boolean;
  deputyFromName: string | null;
  roleDescription: string;
  activeUntil: DateOnly | null;
}

export interface Role {
  id: string;
  name: RoleName;
  isRestrictedToInternalUsers: boolean;
}

export interface RoleWithPermissions {
  id: string;
  name: RoleName;
  isRestrictedToInternalUsers: boolean;
  permissions: Permission[];
}

export interface User {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  organizationUnit: string;
  mobileNumber: string;
  mail: string;
  isInternal: boolean;
  isActive: boolean;
  activeUntil: DateOnly | null;
}

export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  externalId: string;
  mobileNumber: string;
  mail: string;
  organizationUnit: string;
  isInternal: boolean;
  isActive: boolean;
  language: UserLanguage;
  areMailNotificationsOn: boolean;
  notificationInterval: UserNotificationInterval;
  thumbnailPhoto: string;
  lastSuccessfulLoginAt: Date;
  createdAt: Date;
  deputies: User[];
  changePasswordUrl: string;
  twoFactorToSmsUrl: string;
  twoFactorToAppUrl: string;
  activeUntil: DateOnly | null;
}

export interface UserOverview {
  id: string;
  firstName: string;
  lastName: string;
  organizationUnit: string;
  mobileNumber: string;
  mail: string;
  isInternal: boolean;
  isActive: boolean;
  lastSuccessfulLoginDate: Date;
  accountCreatedDate: Date;
}

export interface TemplateActualisation {
  latestProjectTemplateActualisation: Date;
  latestCustomerSpaceTemplateActualisation: Date;
}

export interface Notification {
  id: string;
  customerSpaceId: string;
  title: string;
  text: string;
  parentName: string;
  parentLink: string;
  parentId: string;
  folderName: string;
  folderLink: string;
  type: NotificationType;
  itemName: string;
  itemId: string;
  directLink: string;
  triggeredByName: string;
  triggeredByFallbackName: string;
  triggeredOn: Date;
  isRead: boolean;
}

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  title: string;
  role: string;
  company: string;
  thumbnailPhoto: string;
  isInternal: boolean;
}

export interface CustomerSpaceState {
  navigated: ResourceState<CustomerSpace>;
  admin: ResourceState<CustomerSpaceAdmin>;
  users: ResourceState<User[]>;
  contacts: ResourceState<Contact[]>;
  all: ResourceState<CustomerSpace[]>;
  count: number;
}

export interface UserState {
  organizationUnits: ResourceState<string[]>;
  activeUsers: ResourceState<User[]>;
  userOverview: ResourceState<UserOverview[]>;
  created: ResourceState<User>;
  profile: ResourceState<UserProfile>;
  currentUserRepresenteeIds: ResourceState<string[]>;
}

export interface AdminState {
  sharepointTemplateExtraction: ResourceState<string>;
  templateActualisation: ResourceState<TemplateActualisation>;
  batchCreation: ResourceState<null>;
}

export interface NotificationState {
  customerSpace: ResourceState<Notification[]>;
  isShowingNotifications: boolean;
}

export interface MobileUploadState {
  loading: ResourceState<MobileUploadDto>;
  upload: ResourceState<null>;
}

export interface CoreState {
  customerSpace: CustomerSpaceState;
  user: UserState;
  admin: AdminState;
  notification: NotificationState;
  mobileUpload: MobileUploadState;
}

export const initialCoreState: CoreState = {
  customerSpace: {
    navigated: {
      data: null!,
      status: ResourceStatuses.NONE,
    },
    admin: {
      data: null!,
      status: ResourceStatuses.NONE,
    },
    users: {
      data: [],
      status: ResourceStatuses.NONE,
    },
    contacts: {
      data: [],
      status: ResourceStatuses.NONE,
    },
    all: {
      data: [],
      status: ResourceStatuses.NONE,
    },
    count: 0,
  },
  user: {
    organizationUnits: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
    activeUsers: {
      data: [],
      error: null!,
      status: ResourceStatuses.NONE,
    },
    userOverview: {
      data: [],
      error: null!,
      status: ResourceStatuses.NONE,
    },
    created: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
    profile: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
    currentUserRepresenteeIds: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
  },
  admin: {
    sharepointTemplateExtraction: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
    templateActualisation: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
    batchCreation: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
  },
  notification: {
    customerSpace: {
      data: [],
      error: null!,
      status: ResourceStatuses.NONE,
    },
    isShowingNotifications: false,
  },
  mobileUpload: {
    loading: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
    upload: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
  },
};

export function getUserDisplayName(user: User): string {
  if (!user) {
    return null!;
  }

  if (user.isInternal) {
    if (!user.organizationUnit || user.organizationUnit === '') {
      return user.name + ' (OBT)';
    }

    return user.name + ' (OBT - ' + user.organizationUnit + ')';
  }

  if (!user.organizationUnit || user.organizationUnit === '') {
    return user.name;
  }

  return user.name + ' (' + user.organizationUnit + ')';
}

export const CORE_STATE_FEATURE_KEY = 'core';
